// Generic foot-of-list pagination for tracking surfaces. Mirrors the
// shape of MilestonePagination / MeetingPagination — caption on the
// left, per-page selector + numbered page buttons on the right — but
// parameterises the noun so insurance, SOW, variation, milestone, meeting,
// and deliverable surfaces can share the same control without re-implementing
// a near-identical component.
//
// The `secondaryCount` prop enables the double-count caption used by
// deliverable ("X schedules · Y deliverables total") and meeting
// ("X schedules · Y meetings total") footers. When absent the caption
// reads the simpler "Showing X–Y of N {noun}" form.

import { ChevronLeft, ChevronRight } from "lucide-react";

export interface TrackingPaginationProps {
  page: number;
  pageSize: number;
  total: number;
  /** Singular noun used in the caption (e.g. `insurance / licence`,
   *  `SOW`, `variation`, `schedule`, `milestone`). Pluralised by
   *  appending `s` unless the noun already ends in `s` (e.g. `SOWs`),
   *  in which case the plural matches the singular. */
  noun: string;
  /** Optional secondary count appended as "· N secondaryNoun total".
   *  Used by deliverable (schedules · deliverables total) and meeting
   *  (schedules · meetings total) footers. */
  secondaryCount?: { value: number; noun: string };
  /** Stable id used for `data-testid` prefixes so consumers can
   *  target the caption / control independently per surface. */
  testIdPrefix: string;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function pluralise(noun: string, n: number): string {
  if (n === 1) return noun;
  // Pre-pluralised forms ending in 's' (e.g. "SOWs") stay as-is.
  // The Insurance / Licence noun ends in 'e' so naive +s works
  // ("insurance / licences" reads acceptably in Australian usage —
  // we treat the slash form as a single phrase).
  if (noun.endsWith("s") || noun.endsWith("S")) return noun;
  return `${noun}s`;
}

function pageRange(totalPages: number, current: number): (number | "…")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const head: (number | "…")[] = [1, 2, 3];
  const tail: (number | "…")[] = [
    totalPages - 2,
    totalPages - 1,
    totalPages,
  ];
  if (current > 4 && current < totalPages - 3) {
    return [...head, "…", current, "…", ...tail];
  }
  return [...head, "…", ...tail];
}

export default function TrackingPagination({
  page,
  pageSize,
  total,
  noun,
  secondaryCount,
  testIdPrefix,
  onPageChange,
  onPageSizeChange,
}: TrackingPaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const from = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, total);
  const pages = pageRange(totalPages, safePage);

  return (
    <div
      data-testid={`${testIdPrefix}-pagination`}
      className="flex flex-wrap items-center justify-between gap-3 py-3"
    >
      <div
        data-testid={`${testIdPrefix}-pagination-caption`}
        className="text-caption text-slate-500 dark:text-slate-400"
      >
        Showing {from}–{to} of {total} {pluralise(noun, total)}
        {secondaryCount !== undefined && (
          <>{" "}· {secondaryCount.value} {pluralise(secondaryCount.noun, secondaryCount.value)} total</>
        )}
      </div>
      <div className="flex items-center gap-3">
        <label className="inline-flex items-center gap-2 text-caption text-slate-600 dark:text-slate-300">
          <span>Per page</span>
          <select
            value={pageSize}
            onChange={(e) => {
              const next = Number(e.target.value);
              // Single update — onPageSizeChange MUST reset page in the
              // parent (matches the MilestonePagination contract). See
              // Pagination.tsx for the stale-closure race that breaks
              // when the consumer tries to call both setters here.
              onPageSizeChange(next);
            }}
            className="input-sm"
          >
            {PAGE_SIZE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </label>

        {total > 0 && (
          <nav className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Previous page"
              disabled={safePage <= 1}
              onClick={() => onPageChange(safePage - 1)}
              className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-600 hover:bg-slate-100 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              <ChevronLeft aria-hidden="true" className="h-3.5 w-3.5" />
            </button>
            {pages.map((p, idx) =>
              p === "…" ? (
                <span
                  key={`ellipsis-${idx}`}
                  className="px-1 text-slate-400 dark:text-slate-500"
                >
                  …
                </span>
              ) : (
                <button
                  key={p}
                  type="button"
                  aria-current={p === safePage ? "page" : undefined}
                  onClick={() => onPageChange(p)}
                  className={
                    p === safePage
                      ? "inline-flex h-7 min-w-[28px] items-center justify-center rounded bg-brand-50 px-2 text-caption font-semibold text-brand-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:bg-brand-950 dark:text-brand-300"
                      : "inline-flex h-7 min-w-[28px] items-center justify-center rounded px-2 text-caption text-slate-600 hover:bg-slate-100 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-300 dark:hover:bg-slate-700"
                  }
                >
                  {p}
                </button>
              ),
            )}
            <button
              type="button"
              aria-label="Next page"
              disabled={safePage >= totalPages}
              onClick={() => onPageChange(safePage + 1)}
              className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-600 hover:bg-slate-100 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />
            </button>
          </nav>
        )}
      </div>
    </div>
  );
}
